import React, { useState, useRef, useCallback } from 'react';
// import ReactCrop from 'react-image-crop';
// import 'react-image-crop/dist/ReactCrop.css';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { Button, Modal } from '../../components';
import css from './ImageCropModal.module.css';

const ASPECT_RATIO = 1;

/**
 * ImageCropModal component for cropping images to 4:3 aspect ratio
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onCropComplete - Function called when crop is complete
 * @param {File} props.imageFile - The image file to crop
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element}
 */
const ImageCropModal = props => {
  const {
    isOpen,
    onClose,
    onCropComplete,
    imageFile,
    className,
    onManageDisableScrolling,
  } = props;

  const [ReactCropComponent, setReactCropComponent] = useState(null);

  React.useEffect(() => {
    import('react-image-crop').then(mod => {
      require('react-image-crop/dist/ReactCrop.css');
      setReactCropComponent(() => mod.default);
    });
  }, []);

  const [crop, setCrop] = useState({
    unit: '%',
    width: 100,
    height: 100,
    x: 0,
    y: 0,
    aspect: ASPECT_RATIO,
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  // Load image when file changes
  React.useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();

      reader.onload = () => {
        setImageSrc(reader.result);
      };

      reader.onerror = (error) => {
        console.error('[ImageCropModal] Error reading image file:', error);
      };

      reader.readAsDataURL(imageFile);
    } else {
      console.log('[ImageCropModal] No imageFile, clearing imageSrc');
      setImageSrc(null);
    }
  }, [imageFile]);

  // Auto-center crop when image loads
  const onImageLoad = useCallback((e) => {
    const img = e.currentTarget;

    // Wait for the image to be fully loaded
    if (!img.complete || img.naturalWidth === 0) {
      return;
    }

    imgRef.current = img;

    const { width, height } = img;

    // Calculate optimal crop size for 1:1 aspect ratio (square)
    let cropWidth, cropHeight;

    if (width / height > ASPECT_RATIO) {
      // Image is wider than 1:1, fit to height
      cropHeight = height;
      cropWidth = height * ASPECT_RATIO;
    } else {
      // Image is taller than 1:1, fit to width
      cropWidth = width;
      cropHeight = width / ASPECT_RATIO;
    }

    // Center the crop
    const x = (width - cropWidth) / 2;
    const y = (height - cropHeight) / 2;

    const newCrop = {
      unit: 'px',
      width: cropWidth,
      height: cropHeight,
      x: Math.max(0, x),
      y: Math.max(0, y),
      aspect: ASPECT_RATIO,
    };

    setCrop(newCrop);
    setCompletedCrop(newCrop);
  }, []);

  // Generate cropped image
  const getCroppedImg = useCallback((crop) => {
    const canvas = canvasRef.current;
    const image = imgRef.current;

    if (!canvas || !image || !crop || !crop.width || !crop.height) {
      console.warn('Missing required elements for cropping:', { canvas: !!canvas, image: !!image, crop: !!crop });
      return Promise.resolve(null);
    }

    // Ensure the image is fully loaded
    if (!image.complete || image.naturalWidth === 0) {
      console.warn('Image not fully loaded');
      return Promise.resolve(null);
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('Could not get canvas context');
      return Promise.resolve(null);
    }

    // Set canvas size to match crop dimensions
    const canvasWidth = Math.round(crop.width * scaleX);
    const canvasHeight = Math.round(crop.height * scaleY);

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Draw the cropped image
    try {
      ctx.drawImage(
        image,
        Math.round(crop.x * scaleX),
        Math.round(crop.y * scaleY),
        Math.round(crop.width * scaleX),
        Math.round(crop.height * scaleY),
        0,
        0,
        canvasWidth,
        canvasHeight
      );
    } catch (error) {
      console.error('Error drawing image to canvas:', error);
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('Failed to create blob from canvas');
          resolve(null);
          return;
        }

        // Create a new file with the original name but cropped content
        const croppedFile = new File([blob], imageFile.name, {
          type: imageFile.type,
          lastModified: Date.now(),
        });

        resolve(croppedFile);
      }, imageFile.type, 0.95);
    });
  }, [imageFile]);

  const handleCropComplete = useCallback(async () => {
    if (!completedCrop || !imgRef.current) {
      console.warn('Cannot crop: missing crop data or image reference');
      return;
    }

    setIsProcessing(true);

    try {
      const croppedFile = await getCroppedImg(completedCrop);
      if (croppedFile) {
        onCropComplete(croppedFile);
        onClose();
      } else {
        console.error('Failed to generate cropped image');
        // You might want to show an error message to the user here
      }
    } catch (error) {
      console.error('Error cropping image:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsProcessing(false);
    }
  }, [completedCrop, getCroppedImg, onCropComplete, onClose]);

  const handleClose = useCallback(() => {
    setImageSrc(null);
    setCrop({
      unit: '%',
      width: 100,
      height: 100,
      x: 0,
      y: 0,
      aspect: ASPECT_RATIO,
    });
    setCompletedCrop(null);
    onClose();
  }, [onClose]);

  const classes = classNames(css.root, className);

  return (
    <Modal
      id="ImageCropModal"
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      className={classes}
      contentClassName={css.content}
    >
      <div className={css.header}>
        <h2 className={css.title}>
          <FormattedMessage id="ImageCropModal.title" />
        </h2>
        <p className={css.subtitle}>
          <FormattedMessage id="ImageCropModal.subtitle" />
        </p>
      </div>

      <div className={css.cropContainer}>
        {imageSrc && ReactCropComponent ? (
          <ReactCropComponent
            crop={crop}
            onChange={(newCrop) => {
              setCrop(newCrop);
            }}
            onComplete={(newCrop) => {
              setCompletedCrop(newCrop);
            }}
            aspect={ASPECT_RATIO}
            className={css.reactCrop}
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Crop preview"
              onLoad={onImageLoad}
              className={css.cropImage}
            />
          </ReactCropComponent>
        ) : (
          <div className={css.loadingMessage}>
            <FormattedMessage id="ImageCropModal.loadingImage" />
          </div>
        )}

        {/* Hidden canvas for generating cropped image */}
        <canvas ref={canvasRef} className={css.hiddenCanvas} />
      </div>

      <div className={css.actions}>
        <Button
          onClick={handleClose}
          className={css.cancelButton}
        >
          <FormattedMessage id="ImageCropModal.cancel" />
        </Button>

        <Button
          onClick={handleCropComplete}
          disabled={!completedCrop || isProcessing}
          inProgress={isProcessing}
          className={css.cropButton}
        >
          <FormattedMessage id="ImageCropModal.cropAndUpload" />
        </Button>
      </div>
    </Modal>
  );
};

export default ImageCropModal;
